import { useEffect, useState, useRef } from 'react'

function useTwoFaCodeExpirationCountdown(expiresAt: Date | null) {
  const [currentTime, setCurrentTime] = useState<Date | null>(null)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const expiresAtTimestampRef = useRef<number | null>(null)
  const expiresAtRef = useRef<Date | null>(null)

  // Convert expiresAt to timestamp for stable comparison
  const expiresAtTimestamp = expiresAt?.getTime() ?? null

  // Store the expiresAt object in ref for use in callbacks
  expiresAtRef.current = expiresAt

  useEffect(() => {
    // Only update if the timestamp actually changed
    if (expiresAtTimestampRef.current === expiresAtTimestamp) {
      return
    }

    expiresAtTimestampRef.current = expiresAtTimestamp

    // Clean up any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    if (expiresAtTimestamp == null) {
      setCurrentTime(null)
      return
    }

    // Create Date object from timestamp
    const expiresAtDate = new Date(expiresAtTimestamp)

    // Set initial time
    const now = new Date()
    setCurrentTime(now)

    // Check if already expired
    if (_checkCodeHasExpired(expiresAtDate, now)) {
      return
    }

    // Start the timer
    timerRef.current = setInterval(() => {
      const updatedTime = new Date()
      setCurrentTime(updatedTime)

      // Stop timer if expired (use ref to get latest expiresAt)
      const currentExpiresAt = expiresAtRef.current
      if (
        currentExpiresAt &&
        _checkCodeHasExpired(currentExpiresAt, updatedTime)
      ) {
        if (timerRef.current) {
          clearInterval(timerRef.current)
          timerRef.current = null
        }
      }
    }, 1000)

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [expiresAtTimestamp])

  const hasCodeExpired =
    currentTime && expiresAtRef.current
      ? _checkCodeHasExpired(expiresAtRef.current, currentTime)
      : false

  return {
    countdown:
      currentTime && expiresAtRef.current
        ? timeout('minutes', expiresAtRef.current, currentTime) +
          ':' +
          timeout('seconds', expiresAtRef.current, currentTime)
        : '',
    hasCodeExpired,
  }
}

export { useTwoFaCodeExpirationCountdown }

function timeout(what: string, expiresAt: Date | null, now: Date) {
  return _timeout(what, expiresAt, now)
}

function _timeout(what: string, codeExpirationDate: Date | null, now: Date) {
  if (!codeExpirationDate) return 0

  const diffMs = codeExpirationDate.getTime() - now.getTime()

  let value: number
  if (what === 'seconds') {
    value = Math.floor((diffMs % (1000 * 60)) / 1000)
  } else if (what === 'minutes') {
    value = Math.floor(diffMs / (1000 * 60))
  } else {
    value = Math.floor(diffMs / 1000)
  }

  // Ensure non-negative
  if (value < 0) {
    return what === 'seconds' ? '00' : 0
  }

  // Format with leading zero if needed
  return value < 10 ? '0' + value : value
}

function _checkCodeHasExpired(codeExpirationDate: Date | null, now: Date) {
  if (!codeExpirationDate) return false
  return codeExpirationDate.getTime() < now.getTime()
}
